import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Grid,
} from "@mui/material";
import { toast } from "react-hot-toast";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import QRCard, { QRCardSkeleton } from "./QRCard";
import QRDetailView from "./QRDetailView";
import ShareQRModal from "./ShareQRModal";

const SearchBar = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  background: "#F5F5F5",
  borderRadius: 12,
  padding: "6px 14px",
  gap: 8,
  width: "100%",
  border: "1px solid transparent",
  transition: "border-color 0.15s ease",
  "&:focus-within": {
    borderColor: "#A259FF",
    background: "#FDFAFF",
  },
}));

const EmptyState = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    py={6}
    gap={1.5}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        bgcolor: "#F5EEFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <QrCode2Icon sx={{ fontSize: 32, color: "#A259FF" }} />
    </Box>
    <Typography
      fontWeight={600}
      fontSize={16}
      sx={{ fontFamily: "Inter, Arial, sans-serif" }}
    >
      no qr codes yet
    </Typography>
    <Typography
      fontSize={13}
      color="#999"
      textAlign="center"
      sx={{ fontFamily: "Inter, Arial, sans-serif", maxWidth: 220 }}
    >
      add a location to automatically generate a qr code for it
    </Typography>
  </Box>
);

const QRCodesSetup = ({ isDesktop = false }) => {
  const queryClient = useQueryClient();
  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedQR, setSelectedQR] = useState(null);
  const [shareQR, setShareQR] = useState(null);
  const debounceTimer = useRef(null);

  // Debounce search input by 350ms before hitting the API
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  const { data: apiData, isLoading } = useQuery(
    ["businessQRCodes", selectedBusinessId, debouncedSearch],
    async ({ signal }) => {
      const params = debouncedSearch ? { search: debouncedSearch } : {};
      const res = await axiosInstance.get(
        ApiEndPoints.QR_CODES.getByBusiness(selectedBusinessId),
        { signal, params }
      );
      // Response: { status, message, data: { data: [...] } }
      const payload = res.data.data;
      return Array.isArray(payload) ? payload : (payload?.data ?? []);
    },
    {
      enabled: !!selectedBusinessId,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const { mutate: regenerate } = useMutation(
    (locationId) =>
      axiosInstance.post(ApiEndPoints.QR_CODES.regenerate(locationId)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["businessQRCodes", selectedBusinessId]);
      },
    }
  );

  const filtered = Array.isArray(apiData) ? apiData : [];

  const handleView = useCallback((qr) => setSelectedQR(qr), []);
  const handleShare = useCallback((qr) => setShareQR(qr), []);

  const handleCopy = useCallback((qr) => {
    const url = qr.qrValue || qr.shortUrl || "";
    navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success("link copied"))
      .catch(() => toast.error("failed to copy link"));
  }, []);

  const handleRegenerate = useCallback(
    (qr, callbacks = {}) => {
      const locationId = qr.locationId?._id || qr.locationId;
      if (locationId) regenerate(locationId, callbacks);
    },
    [regenerate]
  );

  if (selectedQR) {
    return (
      <QRDetailView
        qr={selectedQR}
        onBack={() => setSelectedQR(null)}
        isDesktop={isDesktop}
        onRegenerate={handleRegenerate}
      />
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FAFAFA",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: 1.5,
          bgcolor: "#A259FF",
          gap: 1,
          borderRadius: isDesktop ? "0 12px 0 0" : 0,
        }}
      >
        <QrCode2Icon sx={{ color: "#fff", fontSize: 22 }} />
        <Typography
          fontWeight={600}
          fontSize={16}
          color="#fff"
          sx={{ fontFamily: "Inter, Arial, sans-serif", textTransform: "lowercase" }}
        >
          qr codes
        </Typography>
      </Box>

      {/* Search bar */}
      <Box px={{ xs: 2, sm: 3 }} pt={2} pb={1}>
        <SearchBar>
          <SearchIcon sx={{ fontSize: 18, color: "#999" }} />
          <InputBase
            placeholder="search locations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              fontSize: 13,
              fontFamily: "Inter, Arial, sans-serif",
              "& input": { p: 0 },
            }}
          />
          {search && (
            <IconButton
              size="small"
              onClick={() => setSearch("")}
              sx={{ p: 0.25 }}
              aria-label="clear search"
            >
              ×
            </IconButton>
          )}
        </SearchBar>
      </Box>

      {/* List / Grid */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 2, sm: 3 },
          pb: 3,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {isLoading ? (
          <Grid container spacing={2} mt={0}>
            {[1, 2, 3, 4].map((n) => (
              <Grid item key={n} xs={12} sm={isDesktop ? 6 : 12} lg={isDesktop ? 4 : 12}>
                <QRCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid container spacing={2} mt={0}>
            {filtered.map((qr) => (
              <Grid
                item
                key={qr._id || qr.id}
                xs={12}
                sm={isDesktop ? 6 : 12}
                lg={isDesktop ? 4 : 12}
              >
                <QRCard
                  qr={qr}
                  onView={handleView}
                  onShare={handleShare}
                  onCopy={handleCopy}
                  isDesktop={isDesktop}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Share modal */}
      <ShareQRModal
        open={Boolean(shareQR)}
        onClose={() => setShareQR(null)}
        qr={shareQR}
        onCopied={() => toast.success("link copied")}
      />
    </Box>
  );
};

export default QRCodesSetup;
